import React, { Fragment, useCallback, useEffect, useState } from "react";
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import Grid from "@mui/material/Grid";
import { notBlank } from "../util/ext";
import {
  concat,
  equals,
  filter,
  find,
  lensPath,
  lensProp,
  not,
  propEq,
  set,
} from "ramda";
import TextField from "@mui/material/TextField";
import { makeStyles } from "@mui/styles";
import {
  validateDetailsField,
  validateDetails,
} from "./organizationValidation";
import Button from "@mui/material/Button";
import {
  useBuyingGroups,
  useOrganizationDetailsCreate,
  useOrganizationProfileCreate,
  useOrganizationRolesAdd,
  useUsersWithRole,
} from "./organizationServices";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import Autocomplete from "@mui/material/Autocomplete";
import { Skeleton } from "@mui/material";
import { useErrorBox } from "./edit/use-errorBox";
import LibraryTypeSelect from "./edit/LibraryTypeSelect";
import { OrganizationTypeCodes } from "./OrganizationsBrowse";
import PanelLoadingIndicator from "../components/PanelLoadingIndicator";

const useStyles = makeStyles(theme => ({
  title: {
    fontSize: "1.8rem",
  },

  multiPicker: {
    "& label.MuiFormLabel-root": {
      backgroundColor: "#fff",
      marginLeft: "-5px",
      padding: "0 6px",
    },
  },
}));

const NewOrganizationDialog = ({ open, type, onCancel, onSave }) => {
  const classes = useStyles();

  const [fields, setFields] = useState({
    organizationId: null,
    organizationName: null,
    alias: null,
    active: true,
    userCreationAllowed: true,
    priority: false,
    buyingGroups: [],
    libraryTypeId: null,
    salesPersonAccountId: null,
  });

  const {
    addValidationError,
    removeValidationError,
    setValidationErrors,
    setGeneralError,
    hasValidationError,
    firstValidationError,
    ErrorBox: DialogErrors,
  } = useErrorBox();

  const [buyingGroups, setBuyingGroups] = useState([]);
  const buyingGroupResponse = useBuyingGroups();
  useEffect(() => {
    if (buyingGroupResponse.isSuccess && notBlank(buyingGroupResponse.data)) {
      setBuyingGroups(filter(propEq("active", true), buyingGroupResponse.data));
    }
  }, [buyingGroupResponse.isSuccess, buyingGroupResponse.data]);

  const [salesUsers, setSalesUsers] = useState([]);
  const usersResponse = useUsersWithRole("ROLE_SALES_PERSON");
  const [selectedSalesUser, setSelectedSalesUser] = useState(null);
  useEffect(() => {
    if (usersResponse.isSuccess && notBlank(usersResponse.data)) {
      setSalesUsers(usersResponse.data);
      if (notBlank(fields.salesPersonAccountId)) {
        const user = find(
          propEq("userId", fields.salesPersonAccountId),
          usersResponse.data,
        );

        setSelectedSalesUser(user);
      }
    }
  }, [
    usersResponse.isSuccess,
    usersResponse.data,
    fields.salesPersonAccountId,
  ]);

  const handleBlur = (field, value) => {
    const errors = validateDetailsField(field, value);
    if (notBlank(errors)) {
      addValidationError(errors);
    } else {
      removeValidationError(field);
    }
  };

  const handleTextChange = path => event => {
    const value = event.target.value;
    setFields(prev => set(lensPath(path), value, prev));
  };

  const handleCheckboxChange = field => event => {
    const value = event.target.checked;
    setFields(prev => set(lensProp(field), value, prev));
  };

  const handleBuyingGroupsChange = useCallback((_event, orgs) => {
    setFields(prev => set(lensProp("buyingGroups"), orgs, prev));
  }, []);

  const handleSalesPersonChange = useCallback((_event, user) => {
    setFields(prev => set(lensProp("salesPersonAccountId"), user.userId, prev));
  }, []);

  const createDetails = useOrganizationDetailsCreate();
  const addOrganizationRoles = useOrganizationRolesAdd();
  const createProfile = useOrganizationProfileCreate();
  const [waiting, setWaiting] = useState(false);
  const handleApply = useCallback(async () => {
    const validationErrors = validateDetails(fields);
    if (notBlank(validationErrors)) {
      setValidationErrors(validationErrors);
    } else {
      setWaiting(true);
      const addedRoles = equals(type.value, OrganizationTypeCodes.library)
        ? ["ROLE_LIBRARY"]
        : equals(type.value, OrganizationTypeCodes.buying_group)
        ? ["ROLE_BUYING_GROUP"]
        : equals(type.value, OrganizationTypeCodes.publisher)
        ? ["ROLE_VENDOR"]
        : equals(type.value, OrganizationTypeCodes.hotel)
        ? ["ROLE_HOTEL"]
        : [];
      const roleList = concat(["ROLE_ORGANIZATION"], addedRoles);
      try {
        const org = await createDetails.mutateAsync({
          data: fields,
        });
        await addOrganizationRoles.mutateAsync({
          resourceIds: { orgId: org.organizationId },
          data: roleList,
        });
        const organization = {
          organizationId: org.organizationId,
          name: fields.organizationName,
          libraryTypeId: fields.libraryTypeId,
          salesPersonAccountId: fields.salesPersonAccountId,
        };
        await createProfile.mutateAsync({
          data: organization,
        });
        onSave(org);
      } catch (err) {
        setGeneralError(err);
      } finally {
        setWaiting(false);
      }
    }
  }, [
    type,
    fields,
    createDetails,
    createProfile,
    addOrganizationRoles,
    onSave,
    setValidationErrors,
    setGeneralError,
  ]);

  return (
    <Dialog open={open}>
      <DialogTitle id="edit-organization-detail-dialog-title">
        {`New ${type.display} Organization`}
      </DialogTitle>
      <DialogContent>
        <DialogErrors />
        <Grid container spacing={6}>
          <Grid item sm={12}>
            <TextField
              id="organization-name"
              fullWidth
              label="Organization Name"
              InputProps={{
                classes: { root: classes.title },
              }}
              value={fields.organizationName}
              onChange={handleTextChange(["organizationName"])}
              error={hasValidationError("organizationName")}
              helperText={firstValidationError("organizationName")}
              onBlur={() =>
                handleBlur("organizationName", fields.organizationName)
              }
            />
          </Grid>
          {equals(type.value, OrganizationTypeCodes.library) && (
            <Fragment>
              <Grid item sm={12}>
                <LibraryTypeSelect
                  value={fields.libraryTypeId}
                  onChange={handleTextChange(["libraryTypeId"])}
                  error={hasValidationError("libraryTypeId")}
                  helperText={firstValidationError("libraryTypeId")}
                  onBlur={() =>
                    handleBlur("libraryTypeId", fields.libraryTypeId)
                  }
                />
              </Grid>
              <Grid item sm={12}>
                <Autocomplete
                  id="sales-person"
                  options={salesUsers}
                  getOptionLabel={user => notBlank(user) && `${user.username}`}
                  isOptionEqualToValue={(opt, val) =>
                    notBlank(val) && equals(opt.userId, val.userId)
                  }
                  value={selectedSalesUser}
                  onChange={handleSalesPersonChange}
                  renderInput={params => (
                    <TextField {...params} label="Sales Person" />
                  )}
                />
              </Grid>
            </Fragment>
          )}
          <Grid item sm={12}>
            <TextField
              id="organization-alias"
              fullWidth
              label="Alias"
              InputProps={{
                classes: { root: classes.title },
              }}
              value={fields.alias}
              onChange={handleTextChange(["alias"])}
              error={hasValidationError("alias")}
              helperText={firstValidationError("alias")}
              onBlur={() => handleBlur("alias", fields.alias)}
            />
          </Grid>

          <Grid item sm={4}>
            <FormControlLabel
              control={
                <Checkbox
                  color="primary"
                  checked={fields.priority}
                  onChange={handleCheckboxChange("priority")}
                  name="organization-priority"
                  inputProps={{ "aria-label": "Select" }}
                />
              }
              label="Priority"
            />
          </Grid>

          <Grid item sm={4}>
            <FormControlLabel
              control={
                <Checkbox
                  color="primary"
                  checked={fields.userCreationAllowed}
                  onChange={handleCheckboxChange("userCreationAllowed")}
                  name="organization-userCreationAllowed"
                  inputProps={{ "aria-label": "Select" }}
                />
              }
              label="User Creation Allowed"
            />
          </Grid>

          {not(equals(type.value, OrganizationTypeCodes.buying_group)) && (
            <Grid item xs={12}>
              {notBlank(buyingGroups) ? (
                <Box className={classes.multiPicker}>
                  <Autocomplete
                    multiple
                    id="organizations"
                    options={buyingGroups}
                    getOptionLabel={org =>
                      notBlank(org) && `${org.organizationName}`
                    }
                    isOptionEqualToValue={(opt, val) =>
                      notBlank(val) &&
                      equals(opt.organizationId, val.organizationId)
                    }
                    value={fields.buyingGroups}
                    filterSelectedOptions
                    onChange={handleBuyingGroupsChange}
                    renderInput={params => (
                      <TextField
                        {...params}
                        variant="outlined"
                        label="Buying Groups"
                        placeholder="Select..."
                      />
                    )}
                  />
                </Box>
              ) : (
                <Skeleton variant="text" />
              )}
            </Grid>
          )}
        </Grid>
      </DialogContent>
      <DialogActions>
        {waiting ? (
          <Box display="flex" justifyContent="flex-end" padding={2}>
            <PanelLoadingIndicator size={25} />
          </Box>
        ) : (
          <Fragment>
            <Button onClick={onCancel} color="primary">
              Cancel
            </Button>
            <Button onClick={handleApply} color="primary">
              Apply
            </Button>
          </Fragment>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default NewOrganizationDialog;
